
import { defineComponent, reactive, toRefs, onMounted, watch } from 'vue'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import globalsets from '@/utils/globalsets'
import f10ApiHk from "@/api/f10ApiHk"
import { useRoute } from 'vue-router'

export default defineComponent({
  components: { Charts, Nodata },
  props: {
    itemdata: { type: Object, default: () => ({}) },
    pageSourceData: { type: Object, default: () => ({}) },
    colorList: { type: Object, default: () => ({}) },
    token: {
      type: String,
      default: () => {
        return ''
      }
    },
    theme: {
      type: String,
      default: 'light'
    }
  },
  setup(props) {
    const route = useRoute()
    const state: any = reactive({
      productData: [],
      regionData: [],
      currentProductData: [], // 当前报告期的产品数据
      currentRegionData: [],   // 当前报告期的地区数据
      reportOptions: [],
      selectedReport: "",
      mergedTableData: [],
      spinning: true,
      productChartOption: {},
      regionChartOption: {},
      color: globalsets.getColor(),
      getPopupContainer: (e: HTMLElement) => e.parentNode,
      hasData: false,
      // 列定义
      columns: [
        {
          title: '分类',
          dataIndex: 'category',
          key: 'category',
          width: '90px',
          align: 'left',
          customCell: (record: any) => {
            return { rowSpan: record.rowSpan ?? 0 }
          },
          customRender: ({ text, record }: any) => {
            return {
              children: text,
              attrs: { rowSpan: record.rowSpan ?? 0 }
            }
          }
        },
        {
          title: '业务名称',
          dataIndex: 'projectName',
          key: 'projectName',
          ellipsis: true
        },
        {
          title: '主营收(元)',
          dataIndex: 'revenue',
          key: 'revenue',
          align: 'right'
        },
        {
          title: '收入占比',
          dataIndex: 'proportion',
          key: 'proportion',
          align: 'right'
        }
      ]
    })

    // 报告类型映射
    const reportTypeMap: any = {
      '一季报': '一季报',
      '中报': '中报',
      '三季报(累积)': '三季报(累积)',
      '年报': '年报'
    }

    // 格式化收入显示
    const formatRevenue = (revenue: number) => {
      if (revenue === null || revenue === undefined) return '—'

      const absRevenue = Math.abs(revenue)
      const sign = revenue < 0 ? '-' : ''

      if (absRevenue >= 1e8) return `${sign}${(absRevenue / 1e8).toFixed(2)}亿`
      if (absRevenue >= 1e4) return `${sign}${(absRevenue / 1e4).toFixed(2)}万`
      // 如需千分位可以在这里扩展
      return revenue.toString()
    }

    // 处理API响应数据
    const processApiResponse = (apiData: any) => {
      if (!Array.isArray(apiData)) return []
      return apiData.map((report: any) => ({
        reportType: report.reportType,
        statementYear: report.reportYear,
        revenueTotal: report.revenueTotal,
        list: (report.category || []).map((item: any) => ({
          projectName: item.projectSpeciName || '—',
          revenue: item.revenue || 0,
          proportion: item.proportion || 0
        }))
      }))
    }

    // 获取产品数据
    const fetchProductData = async () => {
      try {
        const res = await f10ApiHk.getBriefBriefManagement({
          data: {
            // market: "HK",
            // symbol: "00700",
            market: route.query.market || 'HK',
            symbol: route.query.code || '00700',
            mangeType: "0", // 按产品
            mangeYear: 1
          },
          headers: { token: props.token || "3d5eff0d3c7ac7f3af30c337dcdf6944" }
        })
        state.productData = processApiResponse((res as any).data)
        console.log('产品数据加载完成:', state.productData)
      } catch (e) {
        console.error('产品数据加载失败:', e)
        state.productData = []
      }
    }

    // 获取地区数据
    const fetchRegionData = async () => {
      try {
        const res = await f10ApiHk.getBriefBriefManagement({
          data: {
            // market: "HK",
            // symbol: "00700",
            market: route.query.market || 'HK',
            symbol: route.query.code || '00700',
            mangeType: "1", // 按地区
            mangeYear: 1
          },
          headers: { token: props.token || "3d5eff0d3c7ac7f3af30c337dcdf6944" }
        })
        state.regionData = processApiResponse((res as any).data)
        console.log('地区数据加载完成:', state.regionData)
      } catch (e) {
        console.error('地区数据加载失败:', e)
        state.regionData = []
      }
    }

    // 生成报告期选项
    const generateReportOptions = () => {
      const all = state.productData.concat(state.regionData)
      const unique = new Set<string>()

      all.forEach((r: any) => {
        const label = `${r.statementYear}年${reportTypeMap[r.reportType] || r.reportType}`
        const value = `${r.statementYear}-${r.reportType}`
        unique.add(JSON.stringify({ label, value }))
      })

      // 报告类型优先级：年报 > 三季报 > 中报 > 一季报
      const reportTypePriority: any = {
        '年报': 4,
        '三季报(累积)': 3,
        '中报': 2,
        '一季报': 1
      }

      state.reportOptions = Array.from(unique).map(s => JSON.parse(s)).sort((a: any, b: any) => {
        const [aYear, aType] = a.value.split('-')
        const [bYear, bType] = b.value.split('-')

        // 先按年份降序排列
        if (Number(aYear) !== Number(bYear)) {
          return Number(bYear) - Number(aYear)
        }

        // 同年份按报告类型优先级降序排列
        const aPriority = reportTypePriority[aType] || 0
        const bPriority = reportTypePriority[bType] || 0
        return bPriority - aPriority
      })

      // 设置默认报告期为最新报告期
      if (state.reportOptions.length && !state.selectedReport) {
        console.log('reportOptions', state.reportOptions)
        state.selectedReport = state.reportOptions[0].value
      }
    }

    // 筛选当前报告期的数据
    const filterCurrentReportData = () => {
      if (!state.selectedReport) return

      const [selectedYear, selectedReportType] = state.selectedReport.split('-')

      // 筛选产品数据
      state.currentProductData = state.productData.filter((r: any) =>
        r.statementYear == selectedYear && r.reportType === selectedReportType
      ).flatMap((r: any) => r.list || [])

      // 筛选地区数据
      state.currentRegionData = state.regionData.filter((r: any) =>
        r.statementYear == selectedYear && r.reportType === selectedReportType
      ).flatMap((r: any) => r.list || [])
    }

    // 构建表格数据
    const buildTableData = () => {
      const newTable: any[] = []
      let rowIndex = 0

      // 添加产品分类数据
      if (state.currentProductData.length) {
        state.currentProductData.forEach((item: any, idx: number) => {
          newTable.push({
            key: `product-${idx}`,
            category: idx === 0 ? '按产品' : '',
            projectName: item.projectName,
            revenue: formatRevenue(item.revenue),
            proportion: `${(item.proportion || 0).toFixed(2)}%`,
            rowSpan: idx === 0 ? state.currentProductData.length : 0,
            rowIndex: rowIndex++
          })
        })
      }

      // 添加地区分类数据
      if (state.currentRegionData.length) {
        state.currentRegionData.forEach((item: any, idx: number) => {
          newTable.push({
            key: `region-${idx}`,
            category: idx === 0 ? '按地区' : '',
            projectName: item.projectName,
            revenue: formatRevenue(item.revenue),
            proportion: `${(item.proportion || 0).toFixed(2)}%`,
            rowSpan: idx === 0 ? state.currentRegionData.length : 0,
            rowIndex: rowIndex++
          })
        })
      }

      state.mergedTableData = newTable
    }

    // 生成图表选项
    const generateChartOptions = () => {
      // const baseLegend = (data: any[]) => ({
      //   show: true,
      //   orient: 'vertical',
      //   left: 250,
      //   top: 'middle',
      //   data: data.map(d => d.projectName || d.Project || d.name || '未知'),
      //   textStyle: {
      //     color: '#d7d7df',
      //     fontSize: 12,
      //     width: 250,
      //     // overflow: 'break'
      //   },
      //   itemWidth: 14,
      //   itemHeight: 14,
      //   formatter: function (name: string) {
      //     // 如果名称太长，进行换行处理
      //     if (name.length > 8) {
      //       return name.substring(0, 8) + '\n' + name.substring(8);
      //     }
      //     return name;
      //   }
      // })
      const baseLegend = (data: any[]) => ({
        show: true, orient: 'vertical', left: 230, top: 'middle',
        data: data.map(d => d.projectName || d.Project || d.name || '未知'),
        textStyle: {
          color: '#d7d7df',
          lineHeight: 20,
          padding: [2, 0, 0, 0] // 上边距
        },
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 8,
        formatter: function (name: string) {
          // 如果名称太长，进行换行处理
          if (name.length >= 6) {
            return name.substring(0, 6) + '\n' + name.substring(6);
          }
          return name;
        }
      })
      // 产品图表
      if (state.currentProductData.length) {
        state.productChartOption = {
          color: state.color,
          legend: baseLegend(state.currentProductData),
          tooltip: {
            trigger: 'item',
            position: function (point: any, params: any, dom: any, rect: any, size: any) {
              // 始终显示在鼠标右边
              return [point[0] + 10, point[1] - size.contentSize[1] / 2];
            },
            formatter: (p: any) => `${p.name}: ${formatRevenue(p.value)} (${p.percent}%)`
          },
          series: [{
            name: '按产品',
            type: 'pie',
            radius: '50%',
            center: ['50%', '50%'],
            data: state.currentProductData.map((i: any, index: number) => ({
              name: i.projectName,
              value: i.revenue,
              itemStyle: {
                color: i.revenue < 0 ? '#FF4D4F' : state.color[index % state.color.length]
              }
            })),
            label: { show: false },
            emphasis: { label: { show: false } }
          }]
        }
      } else {
        state.productChartOption = {}
      }

      // 地区图表
      if (state.currentRegionData.length) {
        state.regionChartOption = {
          color: state.color,
          legend: baseLegend(state.currentRegionData),
          tooltip: {
            trigger: 'item',
            position: function (point: any, params: any, dom: any, rect: any, size: any) {
              // 始终显示在鼠标右边
              return [point[0] + 10, point[1] - size.contentSize[1] / 2];
            },
            formatter: (p: any) => `${p.name}: ${formatRevenue(p.value)} (${p.percent}%)`
          },
          series: [{
            name: '按地区',
            type: 'pie',
            radius: '50%',
            center: ['50%', '50%'],
            data: state.currentRegionData.map((i: any, index: number) => ({
              name: i.projectName,
              value: i.revenue,
              itemStyle: {
                color: i.revenue < 0 ? '#FF4D4F' : state.color[index % state.color.length]
              }
            })),
            label: { show: false },
            emphasis: { label: { show: false } }
          }]
        }
      } else {
        state.regionChartOption = {}
      }
    }

    // 更新所有数据
    const updateAllData = () => {
      state.spinning = true
      try {
        filterCurrentReportData()
        buildTableData()
        generateChartOptions()
        state.hasData = state.productData.length > 0 || state.regionData.length > 0
      } catch (err) {
        console.error(err)
      } finally {
        state.spinning = false
      }
    }

    const handleChange = () => updateAllData()

    onMounted(async () => {
      state.spinning = true
      try {
        // 并行加载产品数据和地区数据
        await Promise.all([fetchProductData(), fetchRegionData()])

        // 确保数据加载完成后再生成选项
        console.log('产品数据:', state.productData)
        console.log('地区数据:', state.regionData)

        generateReportOptions()
        updateAllData()
      } catch (error) {
        console.error('数据加载失败:', error)
      } finally {
        state.spinning = false
      }
    })

    watch(() => state.selectedReport, () => updateAllData())
    watch(() => [state.productData, state.regionData], () => {
      generateReportOptions()
      updateAllData()
    }, { deep: true })

    return {
      ...toRefs(state),
      handleChange,

    }
  }
})
